import type { CreateTicketModel, TicketResponse } from '@acme/domains';
import {
  DropdownSelect,
  FileUpload,
  Textarea,
  type DropdownSelectOption,
} from '@acme/foundation';
import { Button, Label, TextInput } from 'flowbite-react';
import { useState } from 'react';

export interface CreateTicketFormProps {
  categories: DropdownSelectOption[];
  applications?: DropdownSelectOption[];
  createTicket: CreateTicketModel;
  onSuccess?: (ticket: TicketResponse) => void;
}

export const CreateTicketForm: React.FC<CreateTicketFormProps> = ({
  categories,
  applications = [],
  createTicket,
  onSuccess,
}) => {
  const [categoryId, setCategoryId] = useState<string>('');
  const [applicationId, setApplicationId] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isApplicationRequired = categoryId === '10';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message || !subject || !categoryId) {
      setError('Пожалуйста, заполните все обязательные поля');
      return;
    }

    if (isApplicationRequired && !applicationId) {
      setError('Для выбранной категории нужно указать номер заявки');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const ticket = await createTicket({
        categoryId: parseInt(categoryId),
        subject,
        message,
        applicationId: applicationId || undefined,
        files: files.length > 0 ? files : undefined,
      });

      onSuccess?.(ticket);
    } catch (err) {
      setError('Ошибка при создании обращения. Попробуйте позже.');
      console.error('Ошибка создания обращения:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col shadow-md">
      <div className="bg-white border border-gray-200 border-b-0 rounded-t-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Обращение в поддержку
        </h2>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="category"
              className="text-sm font-medium text-gray-600"
            >
              Категория обращения
            </Label>
            <DropdownSelect
              options={categories}
              value={categoryId}
              onChange={(next) => {
                setCategoryId(next);
                setError(null);
              }}
              placeholder="Выберите категорию ошибки"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label
              htmlFor="application"
              className="text-sm font-medium text-gray-600"
            >
              Номер заявки {isApplicationRequired ? '' : '(необязательно)'}
            </Label>
            <DropdownSelect
              clearable
              options={applications}
              value={applicationId}
              onChange={(next) => {
                setApplicationId(next);
                setError(null);
              }}
              placeholder="Выберите номер"
            />
          </div>

          <FileUpload onFileSelect={setFiles} maxSize={10} disabled={loading} />

          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <Label
                htmlFor="subject"
                className="text-sm font-medium text-gray-600"
              >
                Тема
              </Label>
              <TextInput
                id="subject"
                placeholder="Введите тему обращения"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={loading}
                required
                className="bg-gray-50 border-gray-300"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label
                htmlFor="message"
                className="text-sm font-medium text-gray-600"
              >
                Сообщение
              </Label>
              <Textarea
                placeholder="Напишите здесь ваше сообщение"
                value={message}
                onChange={setMessage}
                rows={8}
                required
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-b-lg p-6 flex justify-end">
        <Button
          color="primary"
          type="submit"
          disabled={
            loading || !message || (isApplicationRequired && !applicationId)
          }
          className="px-6 py-2"
        >
          {loading ? 'Отправка...' : 'Отправить обращение'}
        </Button>
      </div>
    </form>
  );
};
